import { Head, usePage } from "@inertiajs/react"
import { useState } from "react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ViewModeToggle } from "@/components/projects/view-mode-toggle"
import { PipeStatusCombobox } from "@/components/projects/pipe-status-combobox"
import { AdvancedFiltersPopover } from "@/components/projects/advanced-filters-popover"
import { SortControls } from "@/components/projects/sort-controls"
import { ProjectFormDialog } from "@/components/projects/project-form-dialog"
import { KanbanBoard } from "@/components/projects/kanban-board"
import { useProjectFilters } from "@/hooks/use-project-filters"
import { STATUS_CONFIG } from "@/lib/project-configs"
import { Plus, Search } from "lucide-react"
import type { Project, SharedProps, ProjectTag, ProjectStatus, PipeStatusRecord } from "@/types"

type Props = {
  projects: Project[]
  total_count: number
  status: ProjectStatus
  base_url: string
  filters: Record<string, string>
  users: { id: string; first_name: string; last_name: string }[]
  teams: { id: string; name: string }[]
  project_tags: ProjectTag[]
  pipe_statuses: PipeStatusRecord[]
}

function ProjectsKanban({ projects, total_count, status, base_url, filters, users, teams, project_tags, pipe_statuses }: Props) {
  const { permissions } = usePage<SharedProps>().props
  const canCreate = permissions?.projects.create

  const [showProjectForm, setShowProjectForm] = useState(false)

  const {
    search,
    setSearch,
    inputRef,
    activePipeStatus,
    setPipeStatusFilter,
    sortColumn,
    sortDirection,
    setSort,
    toggleSortDirection,
    advancedOpen,
    setAdvancedOpen,
    advancedFilters,
    activeAdvancedCount,
    setAdvanced,
    setAdvancedDebounced,
    resetAdvancedFilters,
  } = useProjectFilters({ filters, baseUrl: base_url, urlSuffix: "/kanban" })

  const pageTitle = STATUS_CONFIG[status]?.label || "Projets"

  return (
    <>
      <Head title={`${pageTitle} — Kanban`} />
      <PageHeader title={pageTitle} />
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <ViewModeToggle baseUrl={base_url} />
          {canCreate && (
            <Button onClick={() => setShowProjectForm(true)}>
              <Plus className="size-4" />
              Nouveau projet
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3">
          {pipe_statuses.filter((ps) => ps.parent_status === status).length > 1 && (
            <PipeStatusCombobox value={activePipeStatus} onChange={setPipeStatusFilter} status={status} pipeStatuses={pipe_statuses} />
          )}
          <AdvancedFiltersPopover
            open={advancedOpen}
            onOpenChange={setAdvancedOpen}
            advancedFilters={advancedFilters}
            activeAdvancedCount={activeAdvancedCount}
            setAdvanced={setAdvanced}
            setAdvancedDebounced={setAdvancedDebounced}
            resetAdvancedFilters={resetAdvancedFilters}
            users={users}
            teams={teams}
            project_tags={project_tags}
          />
          <div className="relative flex-1">
            <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
            <Input
              ref={inputRef}
              placeholder="Rechercher par nom ou ville..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <span className="text-sm text-muted-foreground tabular-nums whitespace-nowrap">
            {total_count} résultat{total_count > 1 ? "s" : ""}
          </span>
          <SortControls
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={setSort}
            onToggleDirection={toggleSortDirection}
          />
        </div>

        <KanbanBoard projects={projects} status={status} pipeStatuses={pipe_statuses} />
      </div>

      <ProjectFormDialog
        open={showProjectForm}
        onOpenChange={setShowProjectForm}
        users={users}
        teams={teams}
        project_tags={project_tags}
        pipeStatuses={pipe_statuses}
      />
    </>
  )
}

ProjectsKanban.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default ProjectsKanban
